import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { Field, reduxForm, reset } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import fetch from '../../core/fetch';
import cx from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import Grid from 'react-bootstrap/lib/Grid';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';
import { GoogleReCaptchaProvider } from '@google-recaptcha/react';

import Loader from '../Loader';
import InputFieldComponent from '../Common/FormField/InputFieldComponent';

import validate from './validate';
import messages from '../../locale/messages';
import showToaster from '../../helpers/showToaster';
import { verifyRecaptcha } from '../../helpers/verifyRecaptcha';
import { sendContactEmail as query } from '../../lib/graphql';

import mailLogo from '/public/SiteIcons/mailblack.svg';
import caller from '/public/SiteIcons/callLogo.svg';
import addressLogo from '/public/SiteIcons/address.svg';

import s from './ContactForm.css';
import bt from '../../components/commonStyle.css';

const renderCaptcha = ({ input, meta: { touched, error }, reCaptcha }) => {
  const { formatMessage } = useIntl();
  return (
    <div>
      {reCaptcha?.reCaptchaStatus === 'v2' ? (
        <ReCAPTCHA sitekey={reCaptcha?.v2Sitekey} onChange={input.onChange} />
      ) : (
        <GoogleReCaptchaProvider siteKey={reCaptcha?.v3Sitekey} type="v3" />
      )}
      {touched && error && <span className={s.errorMessage}>{formatMessage(error)}</span>}
    </div>
  );
};

const ContactForm = (props) => {
  const { handleSubmit, submitting, error, intl: { formatMessage }, reCaptcha } = props;
  const dispatch = useDispatch();
  const email = useSelector((state) => state?.siteSettings?.data?.email);
  const phoneNumber = useSelector((state) => state?.siteSettings?.data?.phoneNumber);
  const address = useSelector((state) => state?.siteSettings?.data?.address);

  const [contactLoading, setContactLoading] = useState(false);

  const handleClick = useCallback(
    async (values) => {
      const variables = {
        phoneNumber: values.phoneNumber,
        name: values.name,
        email: values.email,
        ContactMessage: values.ContactMessage
      };

      setContactLoading(true);

      const data = await verifyRecaptcha({ values, reCaptcha });
      const { status } = data;

      if (status === 200) {
        const resp = await fetch('/graphql', {
          method: 'post',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: query,
            variables
          }),
          credentials: 'include'
        });

        const { data } = await resp.json();

        setContactLoading(false);

        const result = data?.sendContactEmail?.status;
        showToaster({
          messageId: result == 200 ? 'sendEmail' : 'commonError',
          toasterType: result == 200 ? 'success' : 'error'
        });

        dispatch(reset('ContactForm'));

        if (reCaptcha?.reCaptchaStatus === 'v2') grecaptcha.reset();
      } else {
        setContactLoading(false);
        showToaster({ messageId: 'commonError', toasterType: 'error' });
        if (reCaptcha?.reCaptchaStatus === 'v2') grecaptcha.reset();
      }
    },
    [dispatch, reCaptcha]
  );

  return (
    <Grid fluid className={'commonWordBreak'}>
      <Row>
        <Col lg={12} md={12} sm={12} xs={12} className={s.marginTop}>
          <div>
            <Col lg={12} md={12} sm={12} xs={12} className={s.space3}>
              <div className={s.space6}>
                <h1 className={s.contactTitle}>
                  <FormattedMessage {...messages.contactFormInformation} />
                </h1>
              </div>
            </Col>
            <Col lg={4} md={4} sm={4} xs={12} className={s.alignCenter}>
              <div className={s.space6}>
                <div>
                  <div className={s.iconMargin}>
                    <img src={mailLogo} className={s.mailIcon} />
                  </div>
                  <div>
                    <h1 className={cx(s.contactTitle, s.subTitleText)}>
                      <FormattedMessage {...messages.contactFormEmail} />
                    </h1>
                    <a href={'mailto:' + email} className={s.linkText} target="_blank">
                      {email}
                    </a>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={4} md={4} sm={4} xs={12} className={s.alignCenter}>
              <div className={s.space6}>
                <div>
                  <div className={s.iconMargin}>
                    <img src={caller} className={s.mailIcon} />
                  </div>
                  <div>
                    <h1 className={cx(s.contactTitle, s.subTitleText)}>
                      <FormattedMessage {...messages.contactFormCall} />
                    </h1>
                    <a href={'tel:' + phoneNumber} className={s.linkText} target="_blank">
                      {phoneNumber}
                    </a>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={4} md={4} sm={4} xs={12} className={s.alignCenter}>
              <div className={s.space6}>
                <div>
                  <div className={s.iconMargin}>
                    <img src={addressLogo} className={s.mailIcon} />
                  </div>
                  <h1 className={cx(s.contactTitle, s.subTitleText)}>
                    <FormattedMessage {...messages.contactFormAddress} />
                  </h1>
                  <h4 className={cx(s.addressText)}>{address}</h4>
                </div>
              </div>
            </Col>
          </div>
        </Col>

        <Col lg={12} md={12} sm={12} xs={12} className={cx(s.marginTop)}>
          <div className={cx(s.formBackground, 'inputFocusColor')}>
            <div className={s.formContainerHeader}>
              <h2 className={s.captionText}>
                <FormattedMessage {...messages.contactForm} />
              </h2>
            </div>
            <div className={s.formContainer}>
              {error && <span className={s.errorMessage}>{formatMessage(error)}</span>}
              <form onSubmit={handleSubmit(handleClick)}>
                <Row className={s.formGroup}>
                  <Col xs={12} sm={6} md={6} lg={6} className={s.noPadding}>
                    <Col xs={12}>
                      <label className={s.labelText}>{formatMessage(messages.Nameincontact)}</label>
                    </Col>
                    <Col xs={12}>
                      <Field
                        name="name"
                        type="text"
                        component={InputFieldComponent}
                        placeholder={formatMessage(messages.Nameincontact)}
                        inputClass={cx('commonPasswordControlInput', s.backgroundTwo, 'backgroundTwoDark', 'darkAvater', 'contactUsControlInputRtl')}
                      />
                    </Col>
                  </Col>
                  <Col xs={12} sm={6} md={6} lg={6} className={s.noPadding}>
                    <Col xs={12}>
                      <label className={s.labelText}>{formatMessage(messages.phoneNumber)}</label>
                    </Col>
                    <Col xs={12}>
                      <Field
                        name="phoneNumber"
                        type="text"
                        component={InputFieldComponent}
                        placeholder={formatMessage(messages.phoneNumber)}
                        inputClass={cx('commonPasswordControlInput', s.backgroundThree, 'darkCall', 'contactUsControlInputRtl')}
                      />
                    </Col>
                  </Col>
                </Row>

                <Row className={s.formGroup}>
                  <Col xs={12}>
                    <label className={s.labelText}>{formatMessage(messages.email)}</label>
                    <Field
                      name="email"
                      type="text"
                      component={InputFieldComponent}
                      placeholder={formatMessage(messages.email)}
                      inputClass={cx('commonPasswordControlInput', s.backgroundOne, 'darkEmail', 'contactUsControlInputRtl')}
                    />
                  </Col>
                </Row>

                <Row className={s.formGroup}>
                  <Col xs={12}>
                    <label className={s.labelText}>{formatMessage(messages.ContactMessage)}</label>
                    <Field
                      name="ContactMessage"
                      type="text"
                      component={InputFieldComponent}
                      componentClass="textarea"
                      inputClass={cx('commonPasswordControlInput', s.backgroundFour, 'darkContact', 'contactUsControlInputRtl2')}
                    />
                  </Col>
                </Row>

                <Row className={s.formGroup}>
                  <Col xs={12} className={s.overFlowHidden}>
                    <Field name="reCaptcha" component={renderCaptcha} reCaptcha={reCaptcha} />
                  </Col>
                </Row>

                <Row className={s.formGroup}>
                  <Col xs={12} className={s.spaceTop3}>
                    <Loader
                      type="button"
                      buttonType="submit"
                      className={cx(bt.btnPrimary, bt.btnLarge, bt.loaderButtonFlex)}
                      disabled={submitting}
                      show={contactLoading}
                      label={formatMessage(messages.sendmail)}
                    />
                  </Col>
                </Row>
              </form>
            </div>
          </div>
        </Col>
      </Row>
    </Grid>
  );
};

ContactForm.propTypes = {
  formatMessage: PropTypes.any,
};

export default reduxForm({
  form: 'ContactForm',
  validate
})(injectIntl(withStyles(s, bt)(ContactForm)));
